import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../Api';
import { useNoIndex } from '../seo/useNoIndex';

/**
 * チームの LP。`https://swaycross.com/team/{code}` 経由で開かれる。
 * code を知っている人 = 招待された人とみなすため、公開/非公開どちらのチームも表示する
 * (`visibility` で公開/非公開ラベルを出し分ける)。
 * - Safari / Slack 等では iOS が Universal Link を intercept し、本体 Sway app に
 *   直接橋渡しする。
 * - LINE 等の in-app browser は Universal Link を発火しないため、「チームに参加する」
 *   ボタンで外部ブラウザ(Safari)に脱出させ、そこで Universal Link を発火させる。
 * - app 未インストール端末向けに「App Store でダウンロード」導線も出す。
 *
 * fetch 元: `${API_BASE_URL}/invitation/public-teams/{code}/`
 */

const APP_STORE_URL = 'https://apps.apple.com/jp/app/sway/id6738626019';
const teamUrl = (code: string) => `https://swaycross.com/team/${encodeURIComponent(code)}`;

interface PublicTeam {
  status?: string;
  id: string;
  name: string | null;
  code: string | null;
  capacity: number | null;
  accent_hex: number | null;
  visibility: string;
  member_uids?: string[];
  member_count?: number;
  member_icon_urls?: string[];
  created_at?: string | null;
  updated_at?: string | null;
}

const TeamPage: React.FC = () => {
  useNoIndex();
  const { code } = useParams<{ code: string }>();
  const [team, setTeam] = useState<PublicTeam | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) {
      setError('コードが指定されていません');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/invitation/public-teams/${encodeURIComponent(code)}/`);
        if (!resp.ok) {
          if (resp.status === 404) {
            setError('このチームは存在しないか、すでに削除されています');
          } else {
            setError(`取得に失敗しました (HTTP ${resp.status})`);
          }
          setLoading(false);
          return;
        }
        const data = (await resp.json()) as PublicTeam;
        if (!cancelled) {
          setTeam(data);
          setLoading(false);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message ?? '取得に失敗しました');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const accentColor =
    team && typeof team.accent_hex === 'number'
      ? `#${team.accent_hex.toString(16).padStart(6, '0').toUpperCase()}`
      : '#69BEFF';

  // 人数は backend が返す member_count を優先し、無ければ member_uids 長で代替。
  const memberCount =
    team?.member_count ?? team?.member_uids?.length ?? 0;
  const memberIconUrls = team?.member_icon_urls ?? [];

  const openInApp = () => {
    if (!code) return;
    const ua = navigator.userAgent || '';
    // LINE 等の in-app browser は Universal Link を発火しないので、外部ブラウザ(Safari)に
    // 脱出させる。openExternalBrowser=1 を見ると LINE は system のブラウザで開き直し、
    // そこで iOS が Universal Link を intercept して本体 app に橋渡しする。
    const isLineInApp = /Line\//i.test(ua);
    if (isLineInApp) {
      window.location.href = `${teamUrl(code)}?openExternalBrowser=1`;
      return;
    }
    // Safari / Slack 等は Universal Link をそのまま叩けば iOS が app に橋渡しする。
    window.location.href = teamUrl(code);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top, rgba(105,190,255,0.2), transparent 50%), #000',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Roboto, sans-serif',
      }}
    >
      {loading && (
        <div style={{ opacity: 0.7, fontSize: 14 }}>読み込み中…</div>
      )}

      {error && !loading && (
        <div
          style={{
            textAlign: 'center',
            maxWidth: 420,
          }}
        >
          <div style={{ fontSize: 56, marginBottom: 16 }}>🎧</div>
          <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 8 }}>
            {error}
          </div>
          <a
            href={APP_STORE_URL}
            style={{
              display: 'inline-block',
              marginTop: 24,
              padding: '14px 28px',
              borderRadius: 14,
              background: 'linear-gradient(90deg, #69BEFF, #6D3FF8)',
              color: 'white',
              textDecoration: 'none',
              fontWeight: 'bold',
              fontSize: 16,
            }}
          >
            Sway をダウンロード
          </a>
        </div>
      )}

      {team && !loading && !error && (
        <div
          style={{
            textAlign: 'center',
            maxWidth: 420,
            width: '100%',
          }}
        >
          {/* Team icon (メンバーアイコンの重ねスタック。無ければプレースホルダ) */}
          {memberIconUrls.length > 0 ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto 20px',
              }}
            >
              {memberIconUrls.slice(0, 5).map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt=""
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #000',
                    background: '#222',
                    marginLeft: i === 0 ? 0 : -20,
                  }}
                />
              ))}
              {memberCount > 5 && (
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    border: '2px solid #000',
                    background: '#222',
                    color: 'white',
                    fontSize: 16,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: -20,
                  }}
                >
                  +{memberCount - 5}
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: `${accentColor}40`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}
            >
              <span style={{ fontSize: 56, color: accentColor }}>👥</span>
            </div>
          )}

          <h1
            style={{
              fontSize: 28,
              fontWeight: 'bold',
              margin: '0 0 8px',
            }}
          >
            {team.name}
          </h1>

          <div style={{ opacity: 0.6, fontSize: 14, marginBottom: 6 }}>
            {team.visibility === 'public' ? '公開チーム' : '非公開チーム'}
            {typeof team.capacity === 'number'
              ? ` ・ 定員 ${team.capacity} 人`
              : ' ・ 定員無制限'}
          </div>
          <div
            style={{
              opacity: 0.85,
              color: accentColor,
              fontSize: 13,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              marginBottom: 32,
            }}
          >
            参加コード `{team.code}` ・ {memberCount} 人参加
          </div>

          <button
            onClick={openInApp}
            style={{
              display: 'block',
              width: '100%',
              padding: '16px 24px',
              borderRadius: 14,
              border: 'none',
              background: 'linear-gradient(90deg, #69BEFF, #6D3FF8)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: 17,
              cursor: 'pointer',
              marginBottom: 12,
            }}
          >
            チームに参加する
          </button>

          <a
            href={APP_STORE_URL}
            style={{
              display: 'block',
              padding: '14px 24px',
              borderRadius: 14,
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'white',
              textDecoration: 'none',
              fontWeight: 'semibold',
              fontSize: 15,
              opacity: 0.85,
            }}
          >
            アプリ未インストールの場合は こちらから
          </a>

          <div
            style={{
              marginTop: 40,
              fontSize: 11,
              opacity: 0.4,
            }}
          >
            Sway は音楽と場所でつながる SNS です
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPage;
